import { TaskSummary, TaskType } from "@common/types/task";
import {
    TaskTemplateAssigneeRole,
    TaskTemplateDeadlineAnchor,
} from "@common/types/taskTemplate";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { ViewConfig } from "@lib/views";
import { CloseTemplateTaskDragHandleCell } from "./cells/CloseTemplateTaskDragHandleCell";
import { CloseTemplateTaskNameCell } from "./cells/CloseTemplateTaskNameCell";
import { CloseTemplateTaskActionsCell } from "./cells/CloseTemplateTaskActionsCell";
import { CloseTemplateTaskAssigneeCell } from "./cells/CloseTemplateTaskAssigneeCell";
import { CloseTemplateTaskTypeCell } from "./cells/CloseTemplateTaskTypeCell";
import { CloseTemplateTaskCloseStageCell } from "./cells/CloseTemplateTaskCloseStageCell";
import { CloseTemplateTaskDeadlineCell } from "./cells/CloseTemplateTaskDeadlineCell";
import { CloseTemplateTaskAnchorDateCell } from "./cells/CloseTemplateTaskAnchorDateCell";
import { CloseTemplateTaskEstimateCell } from "./cells/CloseTemplateTaskEstimateCell";

export enum CloseTemplateTaskColumn {
    DRAG_HANDLE = "dragHandle",
    NAME = "name",
    ASSIGNEE = "assignee",
    TASK_TYPE = "taskType",
    CLOSE_STAGE = "closeStage",
    DEADLINE = "deadline",
    ANCHOR_DATE = "anchorDate",
    ESTIMATE = "estimate",
    ACTIONS = "actions",
}

export interface CloseTemplateTaskCallbacks {
    onRemoveTask: (localId: string) => void;
    onEditTask: (localId: string) => void;
    onUpdateAssigneeRole: (
        localId: string,
        assigneeRole: TaskTemplateAssigneeRole | null,
    ) => void;
    onUpdateCloseStage: (localId: string, closeStageId: string | null) => void;
    onUpdateDeadline: (
        localId: string,
        externalDeadline: number | null,
    ) => void;
    onUpdateDeadlineAnchor: (
        localId: string,
        deadlineAnchor: TaskTemplateDeadlineAnchor | null,
    ) => void;
    onUpdateEstimatedTime: (
        localId: string,
        estimatedTime: number | null,
    ) => void;
    onAddTaskForStage: (closeStageId: string | null) => void;
    onBulkSelectStage: (closeStageId: string | null) => void;
    onRemoveStageFromWhitelist: (stageId: string) => void;
}

export interface CloseTemplateTaskMeta {
    sortedStages: OrganizationCloseStage[];
    /** Local IDs of currently selected task rows. */
    selectedLocalIds: Set<string>;
    /** Visible (search-filtered) task local IDs grouped by stage key. */
    taskIdsByStageKey: Map<string, string[]>;
    /** True count of active (non-deleted) tasks per stage key, unaffected by search. */
    activeTaskCountByStageKey: Map<string, number>;
    /** Stage IDs that were explicitly added to the local whitelist. */
    whitelistedStageIds: Set<string>;
    /** Local IDs of tasks whose source task template has a newer version. */
    outdatedLocalIds: Set<string>;
}

export interface CloseTemplateTaskRow {
    id: string;
    name: string;
    closeStageId: string | null;
    assigneeRole: TaskTemplateAssigneeRole | null;
    taskType: TaskType | null;
    externalDeadline: number | null;
    deadlineAnchor: TaskTemplateDeadlineAnchor | null;
    estimatedTime: number | null;
    blockedBy?: TaskSummary[];
    // Group header rows
    isGroupRow?: boolean;
    children?: CloseTemplateTaskRow[];
}

export const closeTemplateTasksViewConfig: ViewConfig<
    CloseTemplateTaskRow,
    CloseTemplateTaskColumn,
    never
> = {
    columns: {
        [CloseTemplateTaskColumn.DRAG_HANDLE]: {
            accessorKey: "id",
            name: "",
            header: "",
            cell: CloseTemplateTaskDragHandleCell,
            immutable: true,
            hiddenFromSettings: true,
            minSize: 40,
            maxSize: 40,
        },
        [CloseTemplateTaskColumn.NAME]: {
            accessorKey: "name",
            name: "Name",
            header: "Name",
            cell: CloseTemplateTaskNameCell,
            immutable: true,
            minSize: 240,
            maxSize: 800,
        },
        [CloseTemplateTaskColumn.ASSIGNEE]: {
            accessorKey: "assigneeRole",
            name: "Role",
            header: "Role",
            cell: CloseTemplateTaskAssigneeCell,
            minSize: 160,
            maxSize: 240,
        },
        [CloseTemplateTaskColumn.TASK_TYPE]: {
            accessorKey: "taskType",
            name: "Workflow",
            header: "Workflow",
            cell: CloseTemplateTaskTypeCell,
            minSize: 130,
            maxSize: 180,
        },
        [CloseTemplateTaskColumn.CLOSE_STAGE]: {
            accessorKey: "closeStageId",
            name: "Stage",
            header: "Stage",
            cell: CloseTemplateTaskCloseStageCell,
            minSize: 150,
            maxSize: 200,
        },
        [CloseTemplateTaskColumn.DEADLINE]: {
            accessorKey: "externalDeadline",
            name: "Due",
            header: "Due",
            cell: CloseTemplateTaskDeadlineCell,
            minSize: 130,
            maxSize: 160,
        },
        [CloseTemplateTaskColumn.ANCHOR_DATE]: {
            accessorKey: "deadlineAnchor",
            name: "Anchor date",
            header: "Anchor date",
            cell: CloseTemplateTaskAnchorDateCell,
            minSize: 160,
            maxSize: 220,
            hiddenFromSettings: true,
        },
        [CloseTemplateTaskColumn.ESTIMATE]: {
            accessorKey: "estimatedTime",
            name: "Estimate",
            header: "Estimate",
            cell: CloseTemplateTaskEstimateCell,
            minSize: 140,
            maxSize: 160,
        },
        [CloseTemplateTaskColumn.ACTIONS]: {
            accessorKey: "id",
            name: "Actions",
            header: "",
            cell: CloseTemplateTaskActionsCell,
            minSize: 120,
            maxSize: 120,
            immutable: true,
            hiddenFromSettings: true,
        },
    },
    nesting: {
        enabled: true,
        expandColumn: CloseTemplateTaskColumn.NAME,
        childrenKey: "children",
    },
};
